import template from 'url-template';

import { get, isEmpty } from 'lodash';

import {
    FETCH_CLIENT_SETTINGS,
    FETCH_CLIENT_ENABLED_COMMS,
    FETCH_EXTERNAL_STATUSES,
    FETCH_SERVICE_STATUS_SETTINGS,
    SET_CLIENT_SETTINGS_LOADING,
    SET_CLIENT_SETTINGS_SEARCH_VALUE,
    SET_SERVICE_STATUS_SETTINGS_LOADING
} from './actionTypes';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';
import lambdaBaseApi from '../apis/lambda';

import { handleNetworkError } from './applicationMessageActions';

const ClientsUrl = template.parse('/companies/{companyId}/clients{?limit,page,searchFilter,sort}');
const AdvancedCommsUrl = template.parse('/advancedcomms{?clientid[in],pageSize}');
const StatusConfigUrl = template.parse('/advancedcommsstatusconfigs{?pageSize,page}');
const ServiceCallStatusesUrl = template.parse('/servicecalls/statuses');
const ServiceAppointmentsStatuesUrl = template.parse('/serviceappointments/statuses');

const DEFAULT_PAGE_LIMIT = 100;
const DEFAULT_SORT = 'name^asc';

export const fetchClientSettingsEnabledComms = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    const { companyId } = getState().companyInfo;

    options.limit = 25;
    options.page || (options.page = 1);
    options.sort || (options.sort = DEFAULT_SORT);
    options.searchFilter || (options.searchFilter = '');

    try {
        dispatch(setClientSettingsLoading(true));

        const pageCount = await k2aBaseApi.get(
            ClientsUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        const meta = get(pageCount, 'data.meta', {});

        let numberOfPages = Math.round(meta.totalCount / options.limit);

        if (numberOfPages === 0) {
            numberOfPages = 1;
        }

        const ClientsUrlArrays = [];
        for (let i = 1; i <= numberOfPages + 1; i++) {
            options.page = i;
            const response = await k2aBaseApi.get(
                ClientsUrl.expand({
                    companyId,
                    ...options
                }),
                header
            );

            ClientsUrlArrays.push(response);
        }    

        const ClientsUrlArrayData = [];
        for (let i = 0; i <= ClientsUrlArrays.length; i++) {
            ClientsUrlArrayData.push(get(ClientsUrlArrays[i], 'data.data', []));
        }
        
        const clientData = ClientsUrlArrayData.flat();

        const clientIds = [];
        for (let i = 0; i <= ClientsUrlArrayData.length; i++) {
            for (let client in ClientsUrlArrayData[i]) {
                clientIds.push(ClientsUrlArrayData[i][client].id);
            }
        }

        options.pageSize = clientIds.length;
        const advancedCommsResponse = await lambdaBaseApi.get(
            AdvancedCommsUrl.expand({
                'clientid[in]': clientIds,
                ...options
            }),
            header
        );

        const advancedCommsData = get(advancedCommsResponse, 'data.data', []);

        clientData.forEach(client => {
            advancedCommsData.forEach(advComm => {
                if (advComm.clientId === client.id) {
                    client.clientEnabled = advComm.commsEnabled;
                }
            });
        });

        dispatch({
            type:    FETCH_CLIENT_ENABLED_COMMS,
            payload: {
                data: clientData
            }
        });

        dispatch(setClientSettingsLoading(false));
    
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setClientSettingsLoading(true));
    }
};

export const fetchClientSettings = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    const { companyId } = getState().companyInfo;

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.page || (options.page = 1);
    options.sort || (options.sort = DEFAULT_SORT);
    options.searchFilter || (options.searchFilter = '');

    try {
        dispatch(setClientSettingsLoading(true));

        const response = await k2aBaseApi.get(
            ClientsUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        const data = get(response, 'data.data', []);
        const meta = get(response, 'data.meta', {});

        if (isEmpty(data)) {
            dispatch({
                type:    FETCH_CLIENT_SETTINGS,
                payload: {
                    data,
                    meta
                }
            });

            dispatch(setClientSettingsLoading(false));

            return;
        }

        const clientIds = data.map(client => client.id);

        const advancedCommsResponse = await lambdaBaseApi.get(
            AdvancedCommsUrl.expand({
                'clientid[in]': clientIds
            }),
            header
        );

        const advancedCommsPayload = get(advancedCommsResponse, 'data.data', []);

        data.forEach(client => {
            advancedCommsPayload.forEach(advComm => {
                if (advComm.clientId === client.id) {
                    client.clientEnabled = advComm.commsEnabled;
                }
            });
        });

        dispatch({
            type:    FETCH_CLIENT_SETTINGS,
            payload: {
                data,
                meta
            }
        });

        dispatch(setClientSettingsLoading(false));

    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setClientSettingsLoading(true));
    }
};

const fetchExternalStatuses = () => async (dispatch, getState) => {
    const header = await awsHeader();
    const { externalStatuses } = getState().communications;

    if (isEmpty(externalStatuses)) {
        try {
            const serviceCallsResponse = await lambdaBaseApi.get(
                ServiceCallStatusesUrl.expand({}),
                header
            );

            const serviceAppointmentsResponse = await lambdaBaseApi.get(
                ServiceAppointmentsStatuesUrl.expand({}),
                header
            );

            const serviceCallsPayload = get(serviceCallsResponse, 'data.data', []);
            const serviceAppointmentsPayload = get(serviceAppointmentsResponse, 'data.data', []);

            const payload = [...serviceCallsPayload, ...serviceAppointmentsPayload];

            dispatch({
                type: FETCH_EXTERNAL_STATUSES,
                payload
            });
        } catch (error) {
            dispatch(handleNetworkError(error));
        }
    }
};

export const fetchServiceStatusSettings = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.pageSize || (options.pageSize = DEFAULT_PAGE_LIMIT);
    options.page || (options.page = 1);

    try {
        dispatch(setServiceStatusSettingsLoading(true));

        await dispatch(fetchExternalStatuses());
        const { externalStatuses } = getState().communications;

        const response = await lambdaBaseApi.get(
            StatusConfigUrl.expand({
                ...options
            }),
            header
        );

        const data = get(response, 'data.data', []);
        const meta = get(response, 'data.meta', {});

        const getStatusLabel = (externalStatuses, statusId) => {
            let status = '';

            externalStatuses.forEach(item => {
                if (item.id === statusId) {
                    status = item.label;
                }
            });

            return status;
        };

        const getEntityLabel = rawEntity => {
            if (rawEntity.toLowerCase() === 'servicecall') {
                return 'Service Call';
            } else if (rawEntity.toLowerCase() === 'serviceappointment') {
                return 'Service Appointment';
            }

            return '';
        };

        const formattedData = data.map(settings => {
            return {
                id:                   settings.id,
                entity:               getEntityLabel(settings.statusKind),
                status:               getStatusLabel(externalStatuses, settings.statusId),
                statusId:             settings.statusId,
                communicationEnabled: settings.commsEnabled,
                confirmationEnabled:  settings.confirmationRequired,
                alertEnabled:         settings.notifyTechnician
            };
        });

        dispatch({
            type:    FETCH_SERVICE_STATUS_SETTINGS,
            payload: {
                data: formattedData,
                meta
            }
        });

        dispatch(setServiceStatusSettingsLoading(false));

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setClientSettingsSearchValue = payload => async dispatch => {
    dispatch({
        type: SET_CLIENT_SETTINGS_SEARCH_VALUE,
        payload
    });
};

export const setClientSettingsLoading = payload => ({
    type: SET_CLIENT_SETTINGS_LOADING,
    payload
});

export const setServiceStatusSettingsLoading = payload => ({
    type: SET_SERVICE_STATUS_SETTINGS_LOADING,
    payload
});

export const updateClientSettings = (isEnabled, client) => async (dispatch, getState) => {
    const header = await awsHeader();
    const { companyId } = getState().companyInfo;
    const { clientSettings, clientSettingsMeta } = getState().communications;

    try {
        const body = {
            clientId:     client.id,
            commsEnabled: isEnabled,
            companyId
        };

        await lambdaBaseApi.post(
            AdvancedCommsUrl.expand({}),
            body,
            header
        );

        clientSettings.forEach(item => {
            if (item.id === client.id) {
                item.clientEnabled = isEnabled;
            }
        });

        dispatch({
            type:    FETCH_CLIENT_SETTINGS,
            payload: {
                data: clientSettings,
                meta: clientSettingsMeta
            }
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const updateServiceStatusSettings = (switchStates, settings) => async (dispatch, getState) => {
    const header = await awsHeader();
    const { companyId } = getState().companyInfo;
    const { serviceStatusSettings, serviceStatusSettingsMeta } = getState().communications;

    try {
        const body = {
            statusId:             settings.statusId || settings.id,
            statusKind:           _getStatusKind(settings.entity),
            commsEnabled:         switchStates.communicationEnabled,
            confirmationRequired: switchStates.confirmationEnabled,
            notifyTechnician:     switchStates.alertEnabled,
            companyId,
        };

        await lambdaBaseApi.post(
            StatusConfigUrl.expand({}),
            body,
            header
        );

        serviceStatusSettings.forEach(item => {
            if (item.id === settings.id) {
                item.communicationEnabled = switchStates.communicationEnabled;
                item.confirmationEnabled = switchStates.confirmationEnabled;
                item.alertEnabled = switchStates.alertEnabled;
            }
        });

        dispatch({
            type:    FETCH_SERVICE_STATUS_SETTINGS,
            payload: {
                data: serviceStatusSettings,
                meta: serviceStatusSettingsMeta
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

const _getStatusKind = entity => {
    if (entity === 'Service Call') {
        return 'servicecall';
    } else if (entity === 'Service Appointment') {
        return 'serviceappointment';
    }
    return '';
};
